// Verse cache.
//
// Each verse costs three network round trips (Arabic, translation,
// transliteration), so moving back and forth through a surah used to refetch
// everything every time. This keeps recently read verses in memory, collapses
// concurrent requests for the same verse into one, and lets the reader warm up
// the neighbours of the verse on screen.
//
// Memory only: nothing is persisted, so a reload starts from a clean cache.

const MAX_ENTRIES = 300;

const cache = new Map();
const inFlight = new Map();

// The reciter and translation are part of the identity: changing either one
// changes the payload.
const keyFor = (surah, verse, reciter, translation) =>
  `${surah}:${verse}|${reciter}|${translation}`;

// Map preserves insertion order, so the first key is the least recently added.
const evictIfNeeded = () => {
  while (cache.size > MAX_ENTRIES) {
    cache.delete(cache.keys().next().value);
  }
};

const remember = (key, data) => {
  // Re-inserting moves the entry to the end, so reads refresh recency.
  cache.delete(key);
  cache.set(key, data);
  evictIfNeeded();
};

export const isVerseCached = (surah, verse, reciter, translation) =>
  cache.has(keyFor(surah, verse, reciter, translation));

// Fetch through the cache. `fetcher` is injected so this module stays free of
// any network dependency.
export const fetchVerseCached = (fetcher, surah, verse, reciter, translation) => {
  const key = keyFor(surah, verse, reciter, translation);

  const cached = cache.get(key);
  if (cached) {
    remember(key, cached);
    return Promise.resolve(cached);
  }

  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = fetcher(surah, verse, reciter, translation)
    .then((data) => {
      remember(key, data);
      return data;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
};

// Warm a verse without surfacing failures: a prefetch that fails is not an
// error the reader should ever see, the real request will report it.
export const prefetchVerse = (fetcher, surah, verse, reciter, translation) => {
  if (!surah || verse === undefined || verse === null) return;
  if (isVerseCached(surah, verse, reciter, translation)) return;
  fetchVerseCached(fetcher, surah, verse, reciter, translation).catch(() => {});
};

export const clearVerseCache = () => {
  cache.clear();
  inFlight.clear();
};

export const verseCacheSize = () => cache.size;
